import { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  Chip,
  Menu,
  IconButton,
  Dialog,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { List, Column } from '@elementary/common-ui/components/Table';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/20x20/edit.svg';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { GROUP_ID } from '@elementary/common-ui/constants/urlParams';
import { Group } from '../types';
import GroupDrawer from './GroupDrawer';

const Name = styled(Flex)`
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.palette.common.black};
  gap: 8px;
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Text = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.black};
`;

interface GroupsTableProps {
  groups?: Group[];
  search: string;
  onDeleteGroup: (groupId: string) => Promise<void>;
  isDeleting: boolean;
}

const GroupsTable = ({
  groups = [],
  search,
  onDeleteGroup,
  isDeleting,
}: GroupsTableProps) => {
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [, setSearchParams] = useSearchParams();

  const filteredData = useMemo(
    () =>
      search.trim()
        ? groups.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase()),
          )
        : groups,
    [groups, search],
  );

  const columns: Array<Column<Group>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Group',
        filterable: false,
        sortable: false,
        renderer: ({ name, external_manager }) => (
          <FlexColumn sx={{ gap: 0.5, overflow: 'hidden' }}>
            <Name>
              <NameText>{name}</NameText>
              {external_manager && (
                <Chip
                  label={external_manager}
                  variant="outlined"
                  size="medium"
                  bold
                  disabled
                />
              )}
            </Name>
          </FlexColumn>
        ),
        width: 250,
      },
      {
        id: 'description',
        header: 'Description',
        filterable: false,
        sortable: false,
        renderer: ({ description }) => (
          <OverflowTooltip>{description || '-'}</OverflowTooltip>
        ),
      },
      {
        id: 'roles',
        header: 'Roles',
        filterable: false,
        sortable: false,
        renderer: ({ roles }) => (
          <Text>
            {roles.length > 0
              ? roles.map((r) => r.name).join(', ')
              : 'No roles'}
          </Text>
        ),
      },
      {
        id: 'users',
        header: 'Users',
        filterable: false,
        sortable: false,
        renderer: ({ users }) => <Text>{users.length}</Text>,
        width: 100,
      },
      {
        id: 'menu',
        header: '',
        renderer: (group) => (
          <Menu
            closeOnChange
            menuButton={
              <IconButton height="20px">
                <MoreHorizIcon />
              </IconButton>
            }
            options={[
              { label: 'Edit', value: 'edit', icon: <EditIcon /> },
              ...(group.external_manager
                ? []
                : [{ label: 'Delete', value: 'delete', icon: <DeleteIcon /> }]),
            ]}
            onChange={(value) => {
              if (value === 'edit') {
                setSearchParams((prev) => {
                  prev.set(GROUP_ID, group.id);
                  return prev;
                });
              } else if (value === 'delete') {
                setGroupToDelete(group);
              }
            }}
          />
        ),
        filterable: false,
        sortable: false,
        metaColumn: true,
        width: 50,
      },
    ],
    [setGroupToDelete, setSearchParams],
  );

  return (
    <>
      <List
        columns={columns}
        data={filteredData}
        defaultSortKey="name"
        onRowClick={(group) => {
          setSearchParams((prev) => {
            prev.set(GROUP_ID, group.id);
            return prev;
          });
        }}
      />

      <GroupDrawer
        onClose={() => {
          setSearchParams((prev) => {
            prev.delete(GROUP_ID);
            return prev;
          });
        }}
      />

      {groupToDelete && !groupToDelete.external_manager && (
        <Dialog
          open={!!groupToDelete}
          onClose={() => setGroupToDelete(null)}
          onConfirmButtonClick={async () => {
            if (groupToDelete) {
              await onDeleteGroup(groupToDelete.id);
              setGroupToDelete(null);
            }
          }}
          isLoading={isDeleting}
          titleIcon={<DeleteIcon />}
          title="Delete group"
          confirmText="Delete group"
          message={`Are you sure you want to delete "${groupToDelete?.name}"?`}
          secondaryMessage="This action cannot be undone."
          variant="error"
        />
      )}
    </>
  );
};

export default GroupsTable;
